import { useCallback, useEffect, useState } from 'react'
import {
  RequirementPhoto,
  AuditRequirementResponse,
  RequirementFormConfig,
} from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import { useMutation, useQueryClient } from 'react-query'
import { v4 as uuidv4 } from 'uuid'
import { useFormContext } from 'react-hook-form'

export const usePhotos = (
  formConfig: RequirementFormConfig,
  requirementID: string,
  auditID: string
) => {
  const queryClient = useQueryClient()
  const { watch, setValue } = useFormContext()
  const [isTakePhotoDialogOpen, setTakePhotoDialogOpen] = useState(false)
  const [isUploadPhotoDialogOpen, setUploadDialogOpen] = useState(false)
  const [currentPhoto, setCurrentPhoto] = useState<string | null>(null)
  const [currentImagePreview, setCurrentImagePreview] =
    useState<RequirementPhoto | null>(null)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false)
  const [isRemoveCamera, setIsRemoveCamera] = useState<boolean>(false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false)
  const photos = watch('photos') as RequirementPhoto[]

  const onImagePreviewClose = useCallback(() => {
    setIsImagePreviewOpen(false)
    setCurrentImagePreview(null)
  }, [setCurrentImagePreview, setIsImagePreviewOpen])

  const onImagePreviewOpenFactory = useCallback(
    (image: RequirementPhoto) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      setCurrentImagePreview(image)
      setIsImagePreviewOpen(true)
    },
    [setCurrentImagePreview, setIsImagePreviewOpen]
  )

  const onUploadPhotoDialogClose = useCallback(() => {
    setUploadDialogOpen(false)
  }, [setUploadDialogOpen])

  const removeImageMutation = useMutation(
    (imageID: string) =>
      apiJSON(`auditrequirements/${imageID}/file`, {}, { method: 'DELETE' }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(formConfig.invalidateQueriesUrl),
    }
  )

  const onPhotoRemoveFactory = useCallback(
    (photoToRemove: RequirementPhoto) => async () => {
      setValue(
        'photos',
        photos.filter((photo) => photo !== photoToRemove)
      )

      await removeImageMutation.mutateAsync(photoToRemove.imageID)
    },
    [photos, removeImageMutation]
  )

  const onTakePhotoDialogOpen = useCallback(() => {
    setTakePhotoDialogOpen(true)
    setIsRemoveCamera(false)
  }, [])

  const onUploadPhotoDialogOpen = useCallback(() => {
    setUploadDialogOpen(true)
  }, [])

  const savePhotos = useCallback(
    (p: RequirementPhoto[]) => {
      setValue('photos', [...photos, ...p])
    },
    [setValue, photos]
  )

  const onPhotoDialogReset = useCallback(() => {
    setCurrentPhoto(null)
    setIsRemoveCamera(false)
  }, [setCurrentPhoto])

  const uploadPhotosFunction = (photos: string[]) =>
    Promise.all(
      photos.map((photo) =>
        apiJSON(
          `auditrequirements/${auditID}/${requirementID}/image`,
          {
            imageString: photo.replace(/data:image\/.*;base64./, ''),
            imageName: uuidv4(),
          },
          {
            method: 'POST',
          }
        )
      )
    )

  const { mutateAsync: uploadRequirementPhotos } = useMutation(
    uploadPhotosFunction,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(formConfig.invalidateQueriesUrl)
      },
    }
  )

  const onPhotoDialogClose = useCallback(
    async (result: boolean) => {
      if (result && currentPhoto) {
        setIsUploadingPhoto(true)
        const fakeImageID = uuidv4()

        savePhotos([
          {
            imageID: fakeImageID,
            fileName: fakeImageID,
            imageURL: currentPhoto,
          },
        ])
        await uploadRequirementPhotos([currentPhoto])
        setIsUploadingPhoto(false)
      }

      setCurrentPhoto(null)
      setTakePhotoDialogOpen(false)
    },
    [currentPhoto, savePhotos, uploadRequirementPhotos]
  )

  const handleOnTakePhotoAnimationDone = (dataUri: string) => {
    setCurrentPhoto(dataUri)
    setIsRemoveCamera(true)
  }

  return {
    photos,
    removeImageMutation,
    onImagePreviewOpenFactory,
    onPhotoRemoveFactory,
    onTakePhotoDialogOpen,
    onUploadPhotoDialogOpen,
    onPhotoDialogClose,
    handleOnTakePhotoAnimationDone,
    onPhotoDialogReset,
    onImagePreviewClose,
    currentImagePreview,
    setCurrentImagePreview,
    isTakePhotoDialogOpen,
    isUploadPhotoDialogOpen,
    isImagePreviewOpen,
    isRemoveCamera,
    isUploadingPhoto,
    currentPhoto,
    onUploadPhotoDialogClose,
    savePhotos,
  }
}
